import React, { useEffect, useMemo, useRef, useState } from "react";
import { SEND_EMAIL_TEMPLATE } from "../../../../utils/GraphQL/mutations";
import { IS_UNSUB, GET_EMAIL_TEMPLATES } from "../../../../utils/GraphQL/queries";
import "./sendEmailModal.css";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import AdminRadioForm from "../../adminComponents/AdminRadioForm/AdminRadioForm";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import Auth from "../../../../utils/auth";
import SendEmailValidator from "../../../../utils/formValidators/sendEmailFormValidator";
import { useMutation, useLazyQuery, useQuery } from "@apollo/client";
import useMediaQuery from "@mui/material/useMediaQuery";
import capitalizeFirstLetters from "../../../../utils/formatters/capitalizeFirstLetters";
import EmailPreview from "../EmailPreview/EmailPreview";
import OfficeShortcuts from "../OfficeShortcuts/OfficeShortcuts";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";

// The five built-ins, used for the dropdown until the templates query answers
// and as the fallback when it fails, so sending is never blocked by the
// template list (Phase 2, 2026-09-17). Prefills come only from the server.
const FALLBACK_TEMPLATES = [
  { key: "blank", label: "None", linkKind: "none", askReceivedPhoto: false },
  { key: "quoteTemplate", label: "Quote", linkKind: "quote", askReceivedPhoto: true },
  { key: "invoiceTemplate", label: "Invoice", linkKind: "invoice", askReceivedPhoto: false },
  { key: "receiptTemplate", label: "Receipt", linkKind: "receipt", askReceivedPhoto: false },
  { key: "refinishRemTemplate", label: "Refinish Reminder", linkKind: "none", askReceivedPhoto: false },
];

const blankEmailFormTemp = {
  to: "",
  from: "",
  subject: "",
  text: "",
  type: "blank",
  attachArr: [],
  quoteLink: [],
  invoiceLink: [],
  toFirstName: "",
  receivedPhoto: false,
};

const SendEmailModal = ({ changeParent, sendTo, templateObj }) => {
  const { handleSnack } = useSnackbar();
  // The exact complement of sendEmailModal.css's `(max-width: 900px)` inline
  // breakpoint, so the single preview always mounts in the container CSS is
  // showing, including at fractional widths under browser zoom (sign-off
  // F6 / N3).
  const mediaOver900 = !useMediaQuery("(max-width: 900px)");
  const [sendEmailMutation] = useMutation(SEND_EMAIL_TEMPLATE);
  const [unsubCheck] = useLazyQuery(IS_UNSUB);
  const [emailSending, setEmailSending] = useState(false);
  const [recipientUnsub, setRecipientUnsub] = useState(false);
  const [attachUpload, setAttachUpload] = useState(false);
  const [secondQuote, setSecondQuote] = useState(false);
  const [secondQuoteLink, setSecondQuoteLink] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachNames, setAttachNames] = useState([]);
  const [errorsArr, setErrorsArr] = useState([]);
  const [emailForm, setEmailForm] = useState({
    ...blankEmailFormTemp,
  });
  const [cursor, setCursor] = useState(null);
  const toInputRef = useRef();
  const firstNameRef = useRef();
  const attachInputRef = useRef();

  // Email templates Phase 1 (2026-09-16): per-type subject/body prefills come
  // from the server (one source with the templates) instead of literals here.
  // Phase 2 (2026-09-17): the list itself is data (custom templates, edited
  // prefills), fetched cache-and-network so an edit made this session reaches
  // the next open, while a warm cache still answers instantly.
  const { data: templatesData, error: defaultsError } = useQuery(GET_EMAIL_TEMPLATES, {
    variables: { composerOnly: true },
    fetchPolicy: "cache-and-network",
  });
  const templateDefaults = useMemo(
    () =>
      templatesData?.getEmailTemplates?.map((t) => ({
        key: t.key,
        label: t.name,
        subjectDefault: t.subjectDefault,
        bodyDefault: t.bodyDefault,
        linkKind: t.linkKind,
        askReceivedPhoto: !!t.askReceivedPhoto,
      })),
    [templatesData]
  );
  const templateList = templateDefaults?.length ? templateDefaults : FALLBACK_TEMPLATES;
  // A type picked before the defaults land (cold cache; the templateObj
  // effect below selects a type on mount) is remembered and applied when
  // they arrive, without clobbering anything the office has typed meanwhile.
  const pendingPrefillRef = useRef(null);

  useEffect(() => {
    const pending = pendingPrefillRef.current;
    if (!pending || !templateDefaults) {
      return;
    }
    const def = templateDefaults.find((t) => t.key === pending);
    if (!def) {
      return;
    }
    pendingPrefillRef.current = null;
    setEmailForm((prev) => {
      if (prev.type !== pending) {
        return prev;
      }
      return {
        ...prev,
        subject: prev.subject?.length > 0 ? prev.subject : def.subjectDefault,
        text: prev.text?.length > 0 ? prev.text : def.bodyDefault,
      };
    });
  }, [templateDefaults]);

  useEffect(() => {
    const input = toInputRef.current;

    if (input?.setSelectionRange) {
      input?.setSelectionRange(cursor, cursor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toInputRef, emailForm?.to]);

  useEffect(() => {
    const input = firstNameRef.current;

    if (input?.setSelectionRange) {
      input?.setSelectionRange(cursor, cursor);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstNameRef, emailForm?.toFirstName]);

  // Form Validator
  const emailValidate = new SendEmailValidator(emailForm);

  // Which link inputs / radio this template needs (Phase 2: by linkKind, not
  // by hard-coded key, so custom templates get the right inputs too).
  const activeTemplate =
    templateList.find((t) => t.key === emailForm?.type) ||
    FALLBACK_TEMPLATES.find((t) => t.key === emailForm?.type) ||
    { linkKind: "none", askReceivedPhoto: false };
  const selectedTypeListed = templateList.some((t) => t.key === emailForm?.type);

  // This used to also set body.style.overflow from `openSend` — a flag that
  // is never set true, so it only ever ran the "auto" branch. Harmless while
  // the modal owned its own overlay; actively wrong now, since it would
  // release BdrModal's reference-counted scroll lock on mount.
  useEffect(() => {
    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setEmailForm((prev) => ({ ...prev, attachArr: attachments }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  useEffect(() => {
    if (sendTo) {
      handlePassedSendTo(sendTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendTo]);

  useEffect(() => {
    if (templateObj) {
      setEmailForm((prev) => ({ ...prev, ...templateObj }));

      if (templateObj?.type) {
        handleTemplateSelect(templateObj?.type);
      }

      if (templateObj?.to) {
        handlePassedSendTo(templateObj?.to);
      }

      if (templateObj?.attachArr?.length > 0) {
        setAttachments([...templateObj?.attachArr]);
        setAttachNames(["BDR-Invoice.pdf"]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateObj]);

  const getUser = async () => {
    const user = await Auth.getProfile();
    if (user?.data.firstName) {
      const upperName = capitalizeFirstLetters(user?.data.firstName);
      const lowerName = user?.data.firstName?.toLowerCase();

      setEmailForm((prev) => ({
        ...prev,
        from: `${upperName} - BDR Services <${lowerName}@bigginsdoorrefinishing.com>`,
      }));
    }
    return;
  };

  const handleUnsubCheck = async () => {
    const { data } = await unsubCheck({
      variables: {
        contact: emailForm?.to,
      },
    });

    if (typeof data?.isUnsub === "boolean") {
      setRecipientUnsub(data?.isUnsub);
    }
  };

  const closeEmail = () => {
    // No body.style.overflow reset here: BdrModal's scroll lock is
    // reference-counted and releases itself on unmount. Forcing "auto" would
    // unlock the page while a modal underneath this one is still open.
    setEmailForm({
      ...blankEmailFormTemp,
    });
    if (changeParent) {
      changeParent(false);
    }
  };

  const handlePassedSendTo = (recipient) => {
    const emailToSendTo = recipient?.match(/<(.*?)>/)?.[1] || recipient;
    const emailRegex = /.+@.+\..+/;

    if (emailToSendTo && emailRegex.test(emailToSendTo)) {
      const email = emailToSendTo.toLowerCase();

      setEmailForm((prev) => ({ ...prev, to: email }));
    }
  };

  // Prefill subject/text for the picked type from the server-supplied
  // defaults (templateDefs.js on the server). Picking "None" (blank) clears
  // both, as before, because its defaults are empty strings.
  const handleTemplateSelect = (value) => {
    // "None" has constant (empty) defaults, so it clears even before the
    // defaults query has answered.
    if (value === "blank") {
      pendingPrefillRef.current = null;
      setEmailForm((prev) => ({ ...prev, type: value, subject: "", text: "" }));
      return;
    }

    const def = templateDefaults?.find((t) => t.key === value);

    if (!def) {
      pendingPrefillRef.current = value;
      setEmailForm((prev) => ({ ...prev, type: value }));
      return;
    }

    pendingPrefillRef.current = null;
    setEmailForm((prev) => ({
      ...prev,
      type: value,
      subject: def.subjectDefault,
      text: def.bodyDefault,
    }));
  };

  const clearAttachments = () => {
    setAttachments([]);
    setAttachNames([]);
  };

  // Takes a materialized ARRAY, not the input's live FileList. The caller does the materializing
  // (see the onChange below) because it must happen BEFORE the value reset — see that comment.
  // This signature change is the point of the fix: reading `event.target.files` in here, after the
  // handler had already reset the input, is exactly the bug pattern.
  const uploadAttachments = async (files) => {
    clearAttachments();
    const count = files.length;

    // `files?.length === 0` was false when files was undefined, which then fell through to
    // `for await (const file of undefined)` and threw "not iterable".
    if (!count) {
      setAttachUpload(false);
      if (handleSnack) {
        handleSnack(`No files selected`);
      }
      return;
    }

    const toBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });

    for await (const file of files) {
      setAttachNames((prev) => [...prev, file?.name]);
      const base64 = await toBase64(file);
      setAttachments((prev) => [...prev, base64]);
    }

    setAttachUpload(false);
    if (handleSnack) {
      handleSnack(`Attached ${count} ${count > 1 ? "files" : "file"}`);
    }
  };

  const handleChange = (event) => {
    const name = event.target.name;
    let value = event.target.value;
    if (event.target.selectionStart) {
      setCursor(event.target.selectionStart);
    } else {
      setCursor(0);
    }

    if (name === "receivedPhoto") {
      const bool = value === "Yes" ? true : false;

      setEmailForm((prev) => {
        return { ...prev, [name]: bool };
      });

      return;
    }

    if (name === "to") {
      value = value.toLowerCase();

      setEmailForm((prev) => {
        return { ...prev, [name]: value };
      });

      return;
    }

    if (name === "quoteLink") {
      setEmailForm((prev) => {
        return { ...prev, [name]: [value] };
      });

      return;
    }

    if (name === "toFirstName") {
      value = capitalizeFirstLetters(value);

      setEmailForm((prev) => {
        return { ...prev, [name]: value };
      });

      return;
    }

    /*
     * There used to be a `messageInput.current?.scrollIntoView()` here for
     * `name === "text"` — on EVERY keystroke in the message box. It never
     * fired while the ref was pasted across eight fields (each overwriting the
     * last, and `BdrInput` is not a forwardRef, so it was null and the
     * optional call no-opped). Pointing the ref at the message field made it
     * real, and it yanked the modal's scroll to the top of the field on every
     * letter typed. Nothing wants that: clicking into the field already put it
     * on screen.
     */
    setEmailForm((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const clearErrors = () => {
    setErrorsArr([]);
  };

  const handleSecondQuote = (event) => {
    let value = event.target.value;
    value = value.toLowerCase();

    setSecondQuoteLink(value);
  };

  const sendEmail = async () => {
    try {
      const valid = await emailValidate.emailMesValid();

      let links = [...emailForm?.quoteLink] || [];
      if (secondQuote) {
        links = [...emailForm?.quoteLink, secondQuoteLink];
      }

      if (valid === true) {
        const { data } = await sendEmailMutation({
          variables: {
            ...emailForm,
            quoteLink: links,
          },
        });

        if (data?.sendEmailTemp === true) {
          if (handleSnack) {
            handleSnack(`Email sent to ${emailForm.to}`, 700);
          }
          clearAttachments();
          setEmailForm({
            ...blankEmailFormTemp,
          });
          setSecondQuoteLink("");
        }

        if (data?.sendEmailTemp === false) {
          if (handleSnack) {
            handleSnack(`Something went wrong. Please try again.`, 3000);
          }
        }

        setEmailSending(false);
        return;
      }

      setErrorsArr(valid);
      setEmailSending(false);
      return;
    } catch (error) {
      console.log(error?.message || error);
      setEmailSending(false);
      if (handleSnack) {
        handleSnack(`Something went wrong. Please try again.`, 3000);
      }
    }
  };

  return (
    <>
      <BdrModal
        open
        onClose={closeEmail}
        title="Compose Email"
        variant="panel"
        size="xl"
        /* The compose window is never dismissed by a stray click or Esc — a
           half-written email is expensive to lose. Closing goes through the
           header ×, which is what the old bespoke button did (its backdrop
           handler was commented out for the same reason). */
        closeOnBackdrop={false}
        closeOnEsc={false}
        /* Send lives in the shell's action row so it stays on screen while
           the body scrolls, and below 900px it no longer sits under the
           stacked preview. */
        actions={
          <>
            {/* `hidden`, and opened from the paperclip button below rather
                than wrapped in a <label>, so the picker is a real focusable
                button. Kept ahead of the buttons: BdrModal's Tab trap wraps on
                the LAST input/button in the panel, and a hidden one there
                would let Tab walk out of the modal. */}
            <input
              ref={attachInputRef}
              hidden
              multiple
              // Array.from BEFORE the value reset, and the ORDER is load-bearing.
              // `event.target.files` is the input's LIVE FileList, and `value = ""` empties
              // THAT SAME OBJECT in place (Blink's FileInputType::SetValue calls clear() on it;
              // WebKit does the same — Firefox allocates a fresh list, which is why this class
              // of bug reproduces in Chrome and not there). Materializing first is what makes
              // the reset safe.
              //
              // The reset itself is the actual fix here: without it, re-picking the SAME file
              // after "Clear Attached" fires no change event at all (the value is unchanged),
              // so the attachment could not be re-added without choosing a different file.
              //
              // Note uploadAttachments is async: it returns at its first await, so a reset
              // placed after the call — or inside the function — would still empty the list out
              // from under the loop. It has to be here, and it has to be after Array.from.
              onChange={(event) => {
                setAttachUpload(true);
                const files = Array.from(event.target.files || []);
                event.target.value = "";
                uploadAttachments(files);
              }}
              type="file"
              accept="image/*,application/pdf"
              name="emailAttachments"
            />
            <BdrButton
              variant="action-solid"
              iconOnly
              icon={<AttachFileIcon />}
              ariaLabel="Attach files"
              title="Attach images or PDFs"
              onClick={() => attachInputRef.current?.click()}
            />
            <BdrButton
              variant="confirm-solid"
              disabled={emailSending}
              onClick={() => {
                setEmailSending(true);
                sendEmail();
              }}
            >
              Send
            </BdrButton>
          </>
        }
      >
        {attachUpload && <AdminBDRSpinner message={"Uploading..."} />}
        {emailSending && <AdminBDRSpinner message={"Sending..."} />}
        <div className="send-email-layout">
          <div className="send-email-input-box">
            <div className="send-email-header-temp-box">
              <BdrInput
                className="send-email-template-select"
                label="Email Template"
                name="emailTemplate"
                options={[
                  ...templateList.map((t) => ({ value: t.key, label: t.label })),
                  ...(!selectedTypeListed && emailForm?.type
                    ? [
                        {
                          value: emailForm.type,
                          label: `${emailForm.type} (unavailable)`,
                        },
                      ]
                    : []),
                ]}
                value={emailForm?.type || ""}
                onChange={(event) => handleTemplateSelect(event.target.value)}
              />
            </div>
            {(errorsArr?.length > 0 ||
              recipientUnsub ||
              defaultsError ||
              /@yahoo.com$/gi.test(emailForm?.to)) && (
              <div
                className="bdr-alert bdr-alert--danger bdr-alert--start send-email-error-ul"
                role="alert"
              >
                <ul>
                {errorsArr?.map((err, index) => {
                  return <li key={err + index}>{err}</li>;
                })}
                {defaultsError && (
                  <li>
                    Template defaults could not be loaded. Type the subject
                    and message yourself; sending still works.
                  </li>
                )}
                {recipientUnsub && (
                  <li>
                    The Email Recipient has requested to be unsubscribed. Please
                    use another form of contact.
                  </li>
                )}
                {/@yahoo.com$/gi.test(emailForm?.to) && (
                  <li>
                    Please give warning of email going to Yahoo spam inbox
                  </li>
                )}
                </ul>
              </div>
            )}
            <BdrInput
              className="send-email-f-wide"
              inputRef={toInputRef}
              type="text"
              label={`To:`}
              name="to"
              inputMode="text"
              value={emailForm.to}
              onFocus={clearErrors}
              onChange={handleChange}
              onBlur={handleUnsubCheck}
            />
            <BdrInput
              className="send-email-f-wide"
              type="text"
              label={`From:`}
              name="from"
              inputMode="text"
              onFocus={clearErrors}
              value={emailForm.from}
              onChange={handleChange}
            />
            <BdrInput
              className="send-email-f-wide"
              type="text"
              label={`Subject:`}
              name="subject"
              inputMode="text"
              onFocus={clearErrors}
              value={emailForm.subject}
              onChange={handleChange}
            />
            {/* Quote-link templates */}
            {activeTemplate.linkKind === "quote" && (
              <>
                <div className="send-email-quote-link-box">
                  <div className="send-email-quote-link-field">
                    <BdrInput
                      className="send-email-f-full"
                      label="Quote Link (Required)"
                      name="quoteLink"
                      onFocus={clearErrors}
                      value={emailForm.quoteLink}
                      onChange={handleChange}
                    />
                  </div>
                  <BdrButton
                    size="sm"
                    variant={secondQuote ? "default" : "action-solid"}
                    className="send-email-add-quote-btn"
                    onClick={() => {
                      setSecondQuote((prev) => !prev);
                    }}
                  >
                    {secondQuote
                      ? "Remove Additional Quote"
                      : "Add Another Quote"}
                  </BdrButton>
                </div>
                {secondQuote && (
                  <div className="send-email-quote-link-box">
                    <div className="send-email-quote-link-field">
                      <BdrInput
                        className="send-email-f-full"
                        label="Additional Quote Link (Required)"
                        name="secondQuoteLink"
                        onFocus={clearErrors}
                        value={secondQuoteLink}
                        onChange={handleSecondQuote}
                      />
                    </div>
                    <BdrButton
                      size="sm"
                      className="send-email-add-quote-btn"
                      onClick={() => {
                        setSecondQuote((prev) => !prev);
                      }}
                    >
                      Remove Additional Quote
                    </BdrButton>
                  </div>
                )}
                <BdrInput
                  className="send-email-f-wide"
                  inputRef={firstNameRef}
                  label="Client's First Name"
                  name="toFirstName"
                  onFocus={clearErrors}
                  value={emailForm.toFirstName}
                  onChange={handleChange}
                />
                {activeTemplate.askReceivedPhoto && (
                  <AdminRadioForm
                    choiceStateFunction={handleChange}
                    name="receivedPhoto"
                    question={"Have we received a photo of the customer's door?"}
                    formId={"received-photo-radio"}
                    choiceDefault={"No"}
                    choices={["Yes", "No"]}
                  />
                )}
              </>
            )}

            {/* Invoice-link templates */}
            {activeTemplate.linkKind === "invoice" && (
              <>
                <div className="send-email-quote-link-box">
                  {/* <div className="send-email-quote-link-field"> */}
                  <BdrInput
                    className="send-email-f-full"
                    label="Invoice Link (Required)"
                    name="invoiceLink"
                    onFocus={clearErrors}
                    value={emailForm.invoiceLink}
                    onChange={handleChange}
                  />
                  {/* </div> */}
                </div>
                <BdrInput
                  className="send-email-f-wide"
                  label="Client's First Name"
                  name="toFirstName"
                  onFocus={clearErrors}
                  value={emailForm.toFirstName}
                  onChange={handleChange}
                />
              </>
            )}
            {/* Receipt templates (invoice link) */}
            {activeTemplate.linkKind === "receipt" && (
              <>
                <div className="send-email-quote-link-box">
                  {/* <div className="send-email-quote-link-field"> */}
                  <BdrInput
                    className="send-email-f-full"
                    label="Invoice Link (Required)"
                    name="invoiceLink"
                    onFocus={clearErrors}
                    value={emailForm.invoiceLink}
                    onChange={handleChange}
                  />
                  {/* </div> */}
                </div>
                <BdrInput
                  className="send-email-f-wide"
                  label="Client's First Name"
                  name="toFirstName"
                  onFocus={clearErrors}
                  value={emailForm.toFirstName}
                  onChange={handleChange}
                />
              </>
            )}
            <div className="send-email-shortcuts">
              {/* The `bdr-btn` recipe as classes, since OfficeShortcuts
                  renders its own <button>. Solid, because this modal is on
                  the saturated panel surface. */}
              <OfficeShortcuts
                handleSnack={handleSnack}
                passedBtnCss={"bdr-btn bdr-btn--action-solid bdr-btn--sm"}
              />
            </div>
            <BdrInput
              className="send-email-f-wide"
              label="Message"
              name="text"
              onFocus={clearErrors}
              value={emailForm.text}
              onChange={handleChange}
              multiline
              rows={5}
            />
            {attachNames?.length > 0 && (
              <>
                <BdrButton
                  size="sm"
                  className="send-email-modal-attach-clear"
                  onClick={() => {
                    clearAttachments();
                    if (handleSnack) {
                      handleSnack(
                        `${
                          attachNames?.length > 1 ? "Attachments" : "Attachment"
                        } Removed`
                      );
                    }
                  }}
                >
                  Clear Attached
                </BdrButton>
                <h3>Attachments: </h3>
                <span style={{ fontSize: "smaller" }}>
                  (File names will be adjusted upon sending email)
                </span>
                <ul>
                  {attachNames?.map((att, index) => {
                    return <li key={att + index}>{att}</li>;
                  })}
                </ul>
              </>
            )}
            <div className="send-email-preview-cont send-email-less-900">
              <h4 className="bdr-section-panel-title bdr-text-shadow send-email-preview-label">
                Email Preview
              </h4>
              <div
                // ref={emailPreviewBoxBig}
                className="send-email-preview-box"
              >
                {/* One live preview at a time: the server renders it, so
                    mounting it in both layout containers would double every
                    request. CSS still decides which container is visible. */}
                {!mediaOver900 && (
                  <EmailPreview
                    toFirstName={emailForm?.toFirstName}
                    previewType={emailForm?.type}
                    from={emailForm?.from}
                    text={emailForm?.text}
                    quoteLinks={
                      secondQuote
                        ? [...emailForm?.quoteLink, secondQuoteLink]
                        : [...emailForm?.quoteLink]
                    }
                    invoiceLinks={emailForm?.invoiceLink}
                    receivedPhoto={emailForm?.receivedPhoto}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="send-email-preview-cont send-email-greater-900">
          {/* The global section-heading type (with the modal title's shadow):
              a step above the green field labels so it reads as the header of
              the right-hand pane, a step below the modal title. */}
          <h4 className="bdr-section-panel-title bdr-text-shadow send-email-preview-label">
            Email Preview
          </h4>
          <div className="send-email-preview-box">
            {mediaOver900 && (
              <EmailPreview
                toFirstName={emailForm?.toFirstName}
                previewType={emailForm?.type}
                from={emailForm?.from}
                text={emailForm?.text}
                quoteLinks={
                  secondQuote
                    ? [...emailForm?.quoteLink, secondQuoteLink]
                    : [...emailForm?.quoteLink]
                }
                invoiceLinks={emailForm?.invoiceLink}
                receivedPhoto={emailForm?.receivedPhoto}
              />
            )}
          </div>
        </div>
      </div>
      </BdrModal>
    </>
  );
};

export default SendEmailModal;
